import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../environments/environment';
import { SecretService } from './secret.service';
import { io, Socket } from 'socket.io-client';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private static _actualSocket: Socket;
  private isInitialized = false;
  private manuallyDisconnected = false;

  /**
   * Fires when the PMT Controller changes this user's role access.
   *
   * Exposed as a Subject rather than letting callers do `on('acl-updated')`
   * themselves, because the socket OBJECT is replaced on every logout/login
   * (disconnect clears its listeners and connect builds a new one). A listener
   * attached to the old socket would silently stop working after the next
   * sign-in. This subject lives as long as the app does, so a subscriber only
   * ever has to subscribe once.
   */
  private aclUpdated = new Subject<any>();
  readonly aclUpdated$ = this.aclUpdated.asObservable();

  /**
   * Fires when something this workspace reads from the PMT Controller changes —
   * a plan, a variant, a feature, or this client's own record. Same reasoning as
   * aclUpdated above: it outlives the socket object, so subscribing once works.
   */
  private workspaceUpdated = new Subject<any>();
  readonly workspaceUpdated$ = this.workspaceUpdated.asObservable();

  /**
   * Fires when a member's activity or work time changed on the server.
   *
   * Carries { userId, date, reason } and nothing else — no hours, no state. It
   * is a nudge to re-read, not the data: the figures come from the Activity
   * View API, which checks the viewer's role. See helpers/activityEvents.js on
   * the backend for why that split is deliberate.
   *
   * Same reasoning as the two subjects above for being a Subject rather than
   * letting the component call on('activity-changed') itself — the socket
   * object is replaced on every logout/login, so a listener attached to the old
   * one stops firing after the next sign-in, silently.
   */
  private activityChanged = new Subject<any>();
  readonly activityChanged$ = this.activityChanged.asObservable();

  /**
   * Fires when the server has verified this socket's token.
   *
   * Needed because connecting is asynchronous and starts at app bootstrap: a
   * component that joins a room in its own ngOnInit can easily run first, find
   * isConnected() false, and never join. Anything that lives in a room should
   * subscribe to this and (re)join when it fires.
   *
   * Keyed on 'authenticated' rather than 'connect' on purpose — that is the
   * server confirming it verified the JWT, by which point its joinRoom handler
   * is certainly registered.
   */
  private authenticated = new Subject<void>();
  readonly authenticated$ = this.authenticated.asObservable();

  /**
   * "A project's discussion board moved."
   *
   * Carries { projectId, topicId, reason, at } and deliberately nothing else —
   * no message text, no author. The server keeps it that way because its
   * joinRoom handler admits anyone who asks for a room; see the warning box in
   * backend/helpers/discussionEvents.js. The board re-fetches through the API,
   * which is where the access check lives.
   *
   * A Subject for the same reason as the three above: the socket instance is
   * replaced on every reconnect, so a component that called on() directly would
   * go quiet after one.
   */
  private discussionChanged = new Subject<any>();
  readonly discussionChanged$ = this.discussionChanged.asObservable();

  get socket(): Socket {
    return SocketService._actualSocket;
  }

  set socket(socket: Socket) {
    SocketService._actualSocket = socket;
  }

  constructor(
    private http: HttpClient,
    private secretServ: SecretService,
  ) {}

  private initializeSocket(): void {
    const token = localStorage.getItem('token');
    this.manuallyDisconnected = false;

    this.socket = io(environment.socketUrl, {
      auth: { token },
      transports: ['polling', 'websocket'],
      autoConnect: false, // only connect manually
      reconnection: false, // disable auto reconnection
    });

    this.socket.on('connect', () => {
      console.log('[SocketService] Connected:', this.socket.id);
    });

    this.socket.on('authenticated', (data) => {
      console.log('[SocketService] Authenticated:', data);
      this.authenticated.next();
    });

    // Both re-registered here on every new socket, so they survive logout/login.
    this.socket.on('acl-updated', (data) => {
      console.log('[SocketService] Role access changed:', data);
      this.aclUpdated.next(data);
    });

    this.socket.on('workspace-updated', (data) => {
      console.log('[SocketService] Workspace data changed:', data);
      this.workspaceUpdated.next(data);
    });

    this.socket.on('activity-changed', (data) => {
      this.activityChanged.next(data);
    });

    this.socket.on('discussion-changed', (data) => {
      this.discussionChanged.next(data);
    });

    this.socket.on('disconnect', (reason) => {
      if (this.manuallyDisconnected) {
        console.log('[SocketService] Manually disconnected:', reason);
      } else {
        console.warn('[SocketService] Unexpected disconnect:', reason);
        // NO reconnection here to honor user's control
      }
    });

    // Heartbeat ping every 30s
    setInterval(() => {
      if (this.socket?.connected) {
        this.socket.emit('ping', { timestamp: Date.now() });
      }
    }, 30000);
  }

  connect(userId: string): void {
    if (!this.isInitialized) {
      this.initializeSocket();
      this.isInitialized = true;
    }

    if (!this.socket.connected) {
      this.manuallyDisconnected = false;

      this.socket.auth = { token: localStorage.getItem('token') };
      this.socket.connect();

      // Join the users individual room for status and private events
      this.joinRoom(userId);
    } else {
      console.log('[SocketService] Already connected.');
      // Still join the room just in case it was a reconnect or different user
      this.joinRoom(userId);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.manuallyDisconnected = true;
      this.socket.emit('logout');
      this.socket.disconnect();
      this.socket.removeAllListeners();
      this.isInitialized = false;
      console.log('[SocketService] Disconnected and listeners cleared.');
    }
  }

  isConnected(): boolean {
    return !!this.socket?.connected;
  }

  emit(event: string, data: any): void {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`[SocketService] Emit failed - not connected: "${event}"`);
    }
  }

  on(event: string, callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event: string): void {
    if (this.socket) {
      this.socket.off(event);
    }
  }

  onNotification(callback: (data: any) => void): void {
    this.off('notification');
    this.on('notification', callback);
  }

  observeNotification(): Observable<any> {
    return new Observable((observer) => {
      this.on('notification', (data) => observer.next(data));
    });
  }

  joinRoom(room: string, onJoinedCb?: (roomId: string) => void): void {
    this.socket.off('joinedRoom');
    this.socket.on('joinedRoom', (joinedRoomId) => {
      if (onJoinedCb) onJoinedCb(joinedRoomId);
    });
    this.socket.emit('joinRoom', room);
  }

  leaveRoom(room: string, onLeftCb?: (roomId: string) => void): void {
    this.socket.off('leftRoom');
    this.socket.on('leftRoom', (leftRoomId) => {
      if (onLeftCb) onLeftCb(leftRoomId);
    });
    this.socket.emit('leaveRoom', room);
  }

  getSocket(): Socket | undefined {
    return this.socket;
  }
}
